/**
 * Rigging table (FXC-9009 · Rig stage headline instrument).
 *
 * One row per element of the deployed section: an editable DEFLECTION (deg), an editable COVE DEPTH
 * (chords) for the elements that carry a cove, and the LIVE gap / overlap readouts for the slot
 * immediately ahead of that element. This unifies rigging for every element kind — the legacy
 * Operation-driven main (deploy) + flaperon, and the per-element plain-flap (aileron) + slat — so all
 * are riggable from one place, with the slot geometry derived live (forward editing only; the inverse
 * "set a target gap" is the separate Phase-5 full-inverse).
 *
 * Per-kind routing (see useFoilDesign setters):
 *   - main-cove      → no own deflection (the main stays FIXED — buildConfiguration never rotates it;
 *                      shown as —); cove = design.cove back wall x (setCoveBackX).
 *   - vane-bspline   → deflection = operation.deployAngleDeg (setDeployDeg) — the deploy angle moves
 *                      the VANE (the lead element of the deployed assembly that actually rotates),
 *                      NOT the main; no cove.
 *   - flaperon-derived → deflection = operation.flaperonAngleDeg (setFlaperonDeg); no cove.
 *   - plain-flap     → deflection = element.deflectionDeg (setElementDeflection); cove depth =
 *                      element.cove.upperCutX − hingeX (setElementCoveDepth).
 *   - plain-slat     → deflection = element.deflectionDeg (setElementDeflection); no cove.
 *
 * Gap / overlap come from the structured `gapMetrics.slots`: the slot whose `downstreamId` equals the
 * element's id (the slot just upstream of it). Chord-normalised; overlap positive = overlapping.
 */
import type { CSSProperties } from "react";

import {
  type Element,
  type GapMetrics,
  type HighLiftAirfoil,
} from "../geometry";
import type { FoilSetters } from "../foil/useFoilDesign";
import type { HighliftPalette } from "../foil/useHighliftTheme";
import type { MotionGapCheck, MotionGapStatus, SlotMotionHealth } from "../geometry/motionGapCheck";

/** Status hue for the range-of-motion gap check, mapped to V5 palette tokens so it tracks the theme. */
function motionStatusColor(status: MotionGapStatus, palette: HighliftPalette): string {
  return status === "collision" ? palette.warn : status === "choked" ? palette.amber : palette.primary;
}

/** A single editable knob resolved for an element row (value + the setter to commit edits through). */
interface RigKnob {
  value: number;
  step: number;
  decimals: number;
  onChange: (v: number) => void;
}

/** Resolve the deflection knob for an element by kind, or `null` when it has no own deflection. */
function deflectionKnob(el: Element, config: HighLiftAirfoil, setters: FoilSetters): RigKnob | null {
  switch (el.kind) {
    case "vane-bspline":
      // The deploy angle rotates the deployed ASSEMBLY (vane + flaperon); the main stays fixed
      // (buildConfiguration never rotates it). Attribute the deploy angle to the vane — the lead
      // element that actually moves — not the main.
      return { value: config.operation.deployAngleDeg, step: 0.5, decimals: 1, onChange: setters.setDeployDeg };
    case "flaperon-derived":
      return { value: config.operation.flaperonAngleDeg, step: 0.5, decimals: 1, onChange: setters.setFlaperonDeg };
    case "plain-flap":
    case "plain-slat":
    case "slat":
    case "krueger":
      return { value: el.deflectionDeg, step: 0.5, decimals: 1, onChange: (v) => setters.setElementDeflection(el.id, v) };
    case "main-cove":
      // The main contour is fixed by the geometry builder, so it has no deflection (shown as —).
      return null;
  }
}

/** Resolve the cove-depth knob for an element by kind, or `null` when it carries no cove. */
function coveKnob(el: Element, config: HighLiftAirfoil, setters: FoilSetters): RigKnob | null {
  switch (el.kind) {
    case "main-cove":
      // The main's cove is design.cove; its back wall x is the single most-rigging-relevant scalar.
      return { value: config.design.cove.coveX, step: 0.005, decimals: 3, onChange: setters.setCoveBackX };
    case "plain-flap":
      // Cove depth = the lip offset aft of the hinge (the slot width opened by coveFromHinge).
      return {
        value: el.cove.upperCutX - el.hingeX,
        step: 0.005,
        decimals: 3,
        onChange: (v) => setters.setElementCoveDepth(el.id, v),
      };
    case "slat":
      // A proper slat's "cove depth" knob edits the cusped-underside slot depth (the slot ceiling
      // concavity on the slat side) — the most rigging-relevant slot-shaping scalar.
      return {
        value: el.slotDepth,
        step: 0.005,
        decimals: 3,
        onChange: (v) => setters.setElementSlotDepth(el.id, v),
      };
    case "krueger":
      // A SLOTTED Krueger opens a converging throat vs the fixed lower nose — surface its slot `gap`
      // here (rides the same slat-style slotAhead / gapMetrics machinery). A SEALED Krueger has no
      // throat, so it shows a dash (null) — matching the v1 default.
      return el.variant === "slotted"
        ? { value: el.gap ?? 0, step: 0.005, decimals: 3, onChange: (v) => setters.setKruegerField(el.id, "gap", v) }
        : null;
    case "vane-bspline":
    case "flaperon-derived":
    case "plain-slat":
      return null;
  }
}

/** The live gap / overlap for the slot immediately AHEAD of an element (its `downstreamId` slot). */
function slotAhead(el: Element, gapMetrics: GapMetrics): { gap: number; overlap: number } | null {
  const slot = gapMetrics.slots.find((s) => s.downstreamId === el.id);
  if (!slot) return null;
  return { gap: slot.metrics.gap, overlap: slot.metrics.overlap };
}

/** Range-of-motion health for the slot ahead of an element (FXC-9009 #8), or null when there is none. */
function motionSlotAhead(el: Element, motionGap: MotionGapCheck): SlotMotionHealth | null {
  return motionGap.slots.find((s) => s.downstreamId === el.id) ?? null;
}

/** A slot's range-of-motion status from its swept health. */
function motionSlotStatus(s: SlotMotionHealth | null): MotionGapStatus | null {
  if (!s) return null;
  return s.collides ? "collision" : s.choked ? "choked" : "ok";
}

/**
 * The Krueger slot-VARIANT toggle (sealed ⇄ slotted) — a two-button segmented control. The variant is
 * an enum (not a numeric RigKnob), so it needs its own control: sealed is the v1 default (no through
 * gap, dash in the cove column); slotted opens a converging throat (surfaces the `gap` knob). Styled
 * off the V5 palette tokens (active = primary-soft fill / primary ink, like the selected row).
 */
function KruegerVariantToggle({
  el,
  setters,
  palette,
}: {
  el: Extract<Element, { kind: "krueger" }>;
  setters: FoilSetters;
  palette: HighliftPalette;
}) {
  const variant: "sealed" | "slotted" = el.variant ?? "sealed";
  const seg = (active: boolean): CSSProperties => ({
    fontFamily: "var(--v5-font-mono, 'JetBrains Mono', monospace)",
    fontSize: 9,
    lineHeight: 1.4,
    padding: "1px 5px",
    border: "none",
    cursor: "pointer",
    background: active ? palette.primarySoft : "transparent",
    color: active ? palette.primary : palette.ink4,
    fontWeight: active ? 600 : 400,
  });
  return (
    <span
      role="group"
      aria-label={`${el.label} variant`}
      onClick={(e) => e.stopPropagation()}
      style={{
        display: "inline-flex",
        flex: "0 0 auto",
        borderRadius: 4,
        overflow: "hidden",
        border: `1px solid ${palette.line}`,
      }}
    >
      <button type="button" aria-pressed={variant === "sealed"} style={seg(variant === "sealed")} onClick={() => setters.setKruegerVariant(el.id, "sealed")}>
        Sealed
      </button>
      <button type="button" aria-pressed={variant === "slotted"} style={seg(variant === "slotted")} onClick={() => setters.setKruegerVariant(el.id, "slotted")}>
        Slotted
      </button>
    </span>
  );
}

/**
 * The aileron FRISE-VARIANT toggle (Plain ⇄ Frise) — a two-button segmented control modelled on
 * {@link KruegerVariantToggle}. A Frise aileron grows a protruding lower-surface nose horn ahead of
 * the hinge that swings below the wing on trailing-edge-UP deflection (aerodynamic + drag balance /
 * adverse-yaw control). Plain is the default (byte-identical contour); Frise adds the horn. Surfaced
 * on the flaperon-derived (default aileron) + plain-flap rows. Styled off the V5 palette tokens.
 */
function FriseVariantToggle({
  el,
  setters,
  palette,
}: {
  el: Extract<Element, { kind: "flaperon-derived" | "plain-flap" }>;
  setters: FoilSetters;
  palette: HighliftPalette;
}) {
  const frise = el.frise === true;
  const seg = (active: boolean): CSSProperties => ({
    fontFamily: "var(--v5-font-mono, 'JetBrains Mono', monospace)",
    fontSize: 9,
    lineHeight: 1.4,
    padding: "1px 5px",
    border: "none",
    cursor: "pointer",
    background: active ? palette.primarySoft : "transparent",
    color: active ? palette.primary : palette.ink4,
    fontWeight: active ? 600 : 400,
  });
  return (
    <span
      role="group"
      aria-label={`${el.label} aileron variant`}
      data-tour="hlw-frise-toggle"
      onClick={(e) => e.stopPropagation()}
      style={{
        display: "inline-flex",
        flex: "0 0 auto",
        borderRadius: 4,
        overflow: "hidden",
        border: `1px solid ${palette.line}`,
      }}
    >
      <button type="button" aria-pressed={!frise} style={seg(!frise)} onClick={() => setters.setElementFrise(el.id, false)}>
        Plain
      </button>
      <button type="button" aria-pressed={frise} style={seg(frise)} onClick={() => setters.setElementFrise(el.id, true)}>
        Frise
      </button>
    </span>
  );
}

export interface RiggingTableProps {
  palette: HighliftPalette;
  config: HighLiftAirfoil;
  elements: Element[];
  setters: FoilSetters;
  gapMetrics: GapMetrics;
  /** Gap check across the section's range of motion (per-slot min gap + interference) — FXC-9009 #8. */
  motionGap: MotionGapCheck;
  /** The selected element id (drives row emphasis); clicking a row selects it. */
  selectedId: string | null;
  onSelect: (id: string) => void;
}

/**
 * The Rig stage's headline rigging instrument. Renders one row per element with editable deflection +
 * cove depth and live gap / overlap. Forward editing only.
 */
export function RiggingTable({ palette, config, elements, setters, gapMetrics, motionGap, selectedId, onSelect }: RiggingTableProps) {
  const mono: CSSProperties = { fontFamily: "var(--v5-font-mono, 'JetBrains Mono', monospace)" };
  const num: CSSProperties = {
    ...mono,
    width: 52,
    background: palette.surface2,
    border: `1px solid ${palette.line}`,
    color: palette.ink,
    borderRadius: 4,
    padding: "2px 4px",
    fontSize: 11,
    textAlign: "right",
  };
  const cellHead: CSSProperties = { fontSize: 9.5, color: palette.ink4, textTransform: "uppercase", letterSpacing: 0.4, fontWeight: 600 };
  const dash = <span style={{ ...mono, fontSize: 11, color: palette.ink4 }}>—</span>;
  const readout = (v: number): string => (Number.isFinite(v) ? v.toFixed(3) : "—");

  return (
    <div className="hlw-rig-table" style={{ display: "flex", flexDirection: "column", gap: 3 }}>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "minmax(64px, 1.3fr) 62px 62px 52px 52px",
          gap: 6,
          alignItems: "center",
          padding: "0 7px 2px",
        }}
      >
        {/* Abbreviated headers (review fix: 'Deflection'/'Overlap' wrapped
            mid-word / truncated at the default inspector width) — the full
            names live in the title attrs. */}
        <span style={cellHead}>Element</span>
        <span style={{ ...cellHead, textAlign: "right", whiteSpace: "nowrap" }} title="Deflection (deg)">Defl°</span>
        <span style={{ ...cellHead, textAlign: "right", whiteSpace: "nowrap" }} title="Cove depth (chords)">Cove</span>
        <span style={{ ...cellHead, textAlign: "right", whiteSpace: "nowrap" }} title="Slot gap (chords)">Gap</span>
        <span style={{ ...cellHead, textAlign: "right", whiteSpace: "nowrap" }} title="Slot overlap (chords; positive = overlapping)">Ovlp</span>
      </div>
      {elements.map((el) => {
        const defl = deflectionKnob(el, config, setters);
        const cove = coveKnob(el, config, setters);
        const slot = slotAhead(el, gapMetrics);
        const mSlot = motionSlotAhead(el, motionGap);
        const mStatus = motionSlotStatus(mSlot);
        const selected = selectedId === el.id;
        const swatch =
          el.kind === "main-cove"
            ? palette.mainEdge
            : el.kind === "vane-bspline"
              ? palette.vaneEdge
              : el.kind === "plain-slat"
                ? palette.blue
                : el.kind === "slat"
                  ? palette.slatEdge
                  : el.kind === "krueger"
                    ? palette.kruegerEdge
                    : palette.flapEdge;
        return (
          <div
            key={el.id}
            data-element-id={el.id}
            onClick={() => onSelect(el.id)}
            style={{
              display: "grid",
              gridTemplateColumns: "minmax(64px, 1.3fr) 62px 62px 52px 52px",
              gap: 6,
              alignItems: "center",
              padding: "4px 7px",
              borderRadius: 7,
              cursor: "pointer",
              border: `1px solid ${selected ? palette.primary : palette.line}`,
              background: selected ? palette.primarySoft : palette.surface,
            }}
          >
            <span style={{ display: "flex", alignItems: "center", gap: 6, minWidth: 0 }}>
              <span style={{ width: 8, height: 8, borderRadius: 2, flex: "0 0 auto", background: swatch }} />
              <span style={{ fontSize: 12, color: palette.ink, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {el.label}
              </span>
              {el.kind === "krueger" ? <KruegerVariantToggle el={el} setters={setters} palette={palette} /> : null}
              {el.kind === "flaperon-derived" || el.kind === "plain-flap" ? (
                <FriseVariantToggle el={el} setters={setters} palette={palette} />
              ) : null}
            </span>
            <span style={{ textAlign: "right" }}>
              {defl ? (
                <input
                  type="number"
                  aria-label={`${el.label} deflection`}
                  step={defl.step}
                  value={Number(defl.value.toFixed(defl.decimals))}
                  onClick={(e) => e.stopPropagation()}
                  onChange={(e) => defl.onChange(Number(e.target.value))}
                  style={num}
                />
              ) : (
                dash
              )}
            </span>
            <span style={{ textAlign: "right" }}>
              {cove ? (
                <input
                  type="number"
                  aria-label={`${el.label} cove depth`}
                  step={cove.step}
                  value={Number(cove.value.toFixed(cove.decimals))}
                  onClick={(e) => e.stopPropagation()}
                  onChange={(e) => cove.onChange(Number(e.target.value))}
                  style={num}
                />
              ) : (
                dash
              )}
            </span>
            <span
              style={{ ...mono, fontSize: 11, color: palette.ink2, textAlign: "right", display: "inline-flex", alignItems: "center", justifyContent: "flex-end", gap: 4 }}
              title={
                mSlot
                  ? `slot throat ahead: ${readout(slot?.gap ?? NaN)}c now · min ${mSlot.minGap.toFixed(4)}c across motion @ deploy ${mSlot.atDeployDeg.toFixed(0)}° / flaperon ${mSlot.atFlaperonDeg.toFixed(0)}°${mSlot.collides ? " — COLLIDES" : mSlot.choked ? " — choked" : ""}`
                  : "slot throat ahead of this element (chords)"
              }
            >
              {mStatus ? (
                <span style={{ width: 6, height: 6, borderRadius: "50%", flex: "0 0 auto", background: motionStatusColor(mStatus, palette) }} />
              ) : null}
              {slot ? readout(slot.gap) : dash}
            </span>
            <span style={{ ...mono, fontSize: 11, color: palette.ink2, textAlign: "right" }} title="chordwise overlap (positive = overlapping)">
              {slot ? readout(slot.overlap) : dash}
            </span>
          </div>
        );
      })}
      {(() => {
        const fmtRange = (r: [number, number]): string => `${r[0].toFixed(0)}–${r[1].toFixed(0)}°`;
        const worst = motionGap.worstSlot;
        const label =
          motionGap.status === "collision"
            ? "Collision in range of motion"
            : motionGap.status === "choked"
              ? "Slot choked in range of motion"
              : "Clear through range of motion";
        return (
          <div style={{ display: "flex", flexDirection: "column", gap: 2, padding: "4px 7px 0" }}>
            <span style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 10.5, flexWrap: "wrap" }}>
              <span style={{ width: 8, height: 8, borderRadius: "50%", flex: "0 0 auto", background: motionStatusColor(motionGap.status, palette) }} />
              <span style={{ color: palette.ink2, fontWeight: 600 }}>{label}</span>
              <span style={{ ...mono, color: palette.ink4 }}>
                deploy {fmtRange(motionGap.deployRange)} · flaperon {fmtRange(motionGap.flaperonRange)}
              </span>
            </span>
            {worst ? (
              <span style={{ ...mono, fontSize: 10, color: palette.ink4 }}>
                tightest {worst.upstreamId}→{worst.downstreamId} {worst.minGap.toFixed(4)}c @ deploy{" "}
                {worst.atDeployDeg.toFixed(0)}° / flaperon {worst.atFlaperonDeg.toFixed(0)}°
              </span>
            ) : null}
            <span style={{ fontSize: 10, color: palette.ink4 }}>
              Gap / overlap are live; the dot + range check sweep the deploy + flaperon limits (chords).
            </span>
          </div>
        );
      })()}
    </div>
  );
}
